import type { Location, NodeEntry } from 'slate';
import { Editor } from 'slate';

import type { LocationDirection } from './fields';
import type { UUID } from '../../types';
import type { CannedFieldElement } from '../internal';
import { isCannedFieldElement } from '../internal';

export const cannedFieldQueries = {
  cannedFieldEntries: (editor: Editor): NodeEntry<CannedFieldElement>[] => {
    return [
      ...Editor.nodes<CannedFieldElement>(editor, {
        at: [],
        match: isCannedFieldElement,
        mode: 'all',
        voids: true,
      }),
    ];
  },

  /**
   * Find the next placement of `cannedFieldId` *after* a location, wrapping to
   * the start of the document when there is none ahead. Placements the
   * location already sits inside are excluded, so this always moves past the
   * one the caret is on rather than re-finding it.
   *
   * Mirrors `getNextDynamicElement` — a canned field can be placed many times,
   * and jumping to the first placement in document order would drag the user
   * away from the copy they were working in.
   */
  getNextCannedFieldElement: (
    editor: Editor,
    options: {
      cannedFieldId: UUID;
      at?: Location;
      direction?: LocationDirection;
    }
  ): NodeEntry<CannedFieldElement> | undefined => {
    const { at = editor.selection, cannedFieldId, direction = 'after' } = options;
    if (!at) return;

    const isMatch = (node: Parameters<typeof isCannedFieldElement>[0]): boolean =>
      isCannedFieldElement(node) && node.cannedFieldId === cannedFieldId;

    const cannedEntriesAbove = [
      ...Editor.levels(editor, { at, match: isCannedFieldElement, voids: true }),
    ];

    const query = direction === 'after' ? Editor.next : Editor.previous;
    const matchEntry = query<CannedFieldElement>(editor, {
      at,
      match: node =>
        cannedEntriesAbove.every(([cannedNode]) => node !== cannedNode) && isMatch(node),
      mode: 'all',
      voids: true,
    });
    if (matchEntry) return matchEntry;

    // Nothing left in this direction — wrap and search from the far edge.
    const documentLocation =
      direction === 'after' ? Editor.start(editor, []) : Editor.end(editor, []);

    return query<CannedFieldElement>(editor, {
      at: documentLocation,
      match: isMatch,
      mode: 'all',
      voids: true,
    });
  },
};
